import { updateTemplateItem, type TemplateItem } from '@/lib/data';
import { itemLabelKey, itemNotesKey } from '@/lib/presence/fieldKeys';

import { commitTrimmedDraft } from './commitTrimmedDraft';
import { useClaimableDraft } from './fieldPresence/useClaimableDraft';
import { useClaimableField } from './fieldPresence/useClaimableField';
import { useTemplateFieldPresence } from './fieldPresence/useTemplateFieldPresence';
import { StepEditorRow } from './StepEditorRow';

/** A single editable step row (label + reference note) in the template editor. */
export function EditableStep({
  item,
  position,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  item: TemplateItem;
  position: number;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const presence = useTemplateFieldPresence();
  const labelKey = itemLabelKey(item.id);
  const notesKey = itemNotesKey(item.id);

  const labelField = useClaimableField(labelKey);
  const {
    draft: label,
    setDraft: setLabel,
    onFocus: onLabelFocus,
    onBlur: onLabelBlur,
  } = useClaimableDraft(
    labelField,
    item.label,
    commitTrimmedDraft(item.label, (next) =>
      updateTemplateItem(item.id, { label: next }),
    ),
  );
  const noteField = useClaimableField(notesKey);
  const {
    draft: note,
    setDraft: setNote,
    onFocus: onNoteFocus,
    onBlur: onNoteBlur,
  } = useClaimableDraft<string | null>(noteField, item.notes, (draft) => {
    const next = draft && draft.trim() ? draft.trim() : null;
    if (next !== item.notes) updateTemplateItem(item.id, { notes: next });
  });

  // Deleting the step is the structural version of editing its fields, so any
  // claim on the step gates it (advisory, with the Delete-anyway override).
  const stepClaimed = labelField.claims.length > 0 || noteField.claims.length > 0;

  return (
    <StepEditorRow
      id={item.id}
      position={position}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      label={{
        value: label,
        onChange: setLabel,
        onFocus: onLabelFocus,
        onBlur: onLabelBlur,
        presence: labelField,
      }}
      onRemove={() => {
        if (stepClaimed) presence.deleteBlockedPress([labelKey, notesKey], onRemove);
        else onRemove();
      }}
      referenceNote={{
        value: note,
        onChange: setNote,
        onFocus: onNoteFocus,
        onBlur: onNoteBlur,
        onAdd: () => setNote(''),
        onRemove: () => {
          setNote(null);
          noteField.inputRemoved();
          if (item.notes != null) updateTemplateItem(item.id, { notes: null });
        },
        presence: noteField,
      }}
      removeClaimed={stepClaimed}
    />
  );
}
